import React, {
    useReducer,
    useContext,
    useEffect,
    useCallback,
    useState,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
    useApolloClient, useLazyQuery, useMutation, useQuery,
    useReactiveVar, gql,
} from '@apollo/client';
import queryString from 'query-string';
import classNames from 'classnames';
import Raven from 'raven-js';

import { paymentService } from 'global/services/paymentService/paymentService';
import { analyticService } from 'global/services';

import {
    SHIPPING,
} from 'app/graphql/network/checkout/checkoutMutations';

import formatPrice from 'app/utils/price';

import {
    getDishesFromBasketSections,
} from 'app/utils/dish';
import { normalizePhone, getDigitsFromPhone } from 'app/utils/phone';

import { LocaleContext } from 'app/containers/LocaleProvider';

import { Mobile, Desktop } from 'app/components/Responsive';
import TelephoneInput from 'app/components/Form/Telephone';
import ApartmentInput from 'app/components/Form/Apartment';
import EntranceInput from 'app/components/Form/Entrance';
import { UIButton } from 'app/components/ui';

import { LocationContext } from 'app/containers/LocationProvider';
import { checkoutDeliveryDateVar, isAsNewbieAuthedVar } from 'app/apollo/reaction';
import { BillingContext } from '../BillingProvider';
import { BasketContext } from '../BasketProvider';
import { UserContext } from '../UserProvider';
import { ConfigContext } from '../ConfigProvider';
import { FiltersContext } from '../FiltersProvider';

import AddressSelect from '../AddressSelect';
import DeliveryDateTime from '../DeliveryDateTime';

import { CheckoutPromocode } from './CheckoutPromocode/CheckoutPromocode';
import { CheckoutEmail } from './CheckoutEmail/CheckoutEmail';
import {
    checkoutReducer,
    createInitialCheckoutState,
    createActions,
} from './checkout-store';
import { PROMOCODE_FROM_CART_QUERY } from './landingCheckout.query';
import { locales } from './promo-checkout.locales';

/* NETWORK */
import { INITIAL_ADDRESS_INPUT_VALUE } from './requests/checkout.lp.graphql';

import './checkout-ny.scss';
import { mapAllDishesTags } from './checkout-utils';
import { useEmail } from './useEmail';
import { NY_DISHES } from 'app/utils/filters';


/**
 * Специфик для /box
 * Меняет цену на главном банере
 *
 * discount-price-node
 * welcom-price-node
 */
const updatePageHeaderPrice = (pageId, totalPrice) => {
    if (pageId !== 'box') {
        return;
    }
    const discountPriceNode = document.querySelectorAll('#discount-price-node');
    const welcomPriceNode = document.querySelectorAll('#welcom-price-node');

    discountPriceNode.forEach((node) => {
        node.innerText = formatPrice(totalPrice);
        node.classList.remove('invisible');
    });

    if (totalPrice < 10000) {
        welcomPriceNode.forEach((node) => node.classList.remove('invisible'));
    }
};


function Checkout({
    parentRef = {},
    onPhoneEntered,
    onPhoneInitialized,
    onInvoiceCreated,
    hasReadyToEatContradiction,
    onLocationChange,
    state,
    dispatch,
}) {
    const { locale } = useContext(LocaleContext);
    const l = locales[locale];

    const config = useContext(ConfigContext);
    const {
        pageId,
        paymentWidgetType,
        city,
        paymentMethod,
        prepareAddressSuggestions,
        ignorePeriodForDeliveryDates,
        checkoutButtonTextType,
        // isFoodcardEnabled, FRNT-3237 Deprecated
        printed_recipes,
    } = config;

    const subdivision = city === 'msk' ? 'RU-MOS' : null;

    const filtersContext = useContext(FiltersContext);
    const { period, setNextAvailablePeriod } = filtersContext;

    const {
        selectDateAction,
        selectTimeIntervalAction,
        changePhoneAction,
        changeAddressAction,
        selectAddressAction,
        changeFlatAction,
        changeEntranceAction,
        initializeDatesAction,
        changePromocodeAction,
        changeLoadingPromocodeAction,
        createInvoice,
    } = createActions({
        config,
        filtersContext,
    });

    const client = useApolloClient();
    const [updateShipping] = useMutation(SHIPPING);

    const { userQuery } = useContext(UserContext);

    const {
        updateBilling,
    } = useContext(BillingContext);

    const {
        basket,
        refetchBasket,
        trialScenario,
    } = useContext(BasketContext);

    const {
        locationKey,
    } = useContext(LocationContext);

    const isFilterForNYDateNeeded = useMemo(
        () => {
            const tagsCollection = mapAllDishesTags(basket?.sections);
            const isNYDishAdded = tagsCollection.some((tag) => tag.id === NY_DISHES);
            return pageId === 'blyuda-dlya-novogodnego-stola'
                && isNYDishAdded
                && !['MSK', 'TUL', 'YAR', 'VLA', 'RYA', 'KLU', 'TVE'].includes(locationKey);
        },
        [basket?.sections, pageId, locationKey],
    );

    const isEmailRequired = useMemo(() => pageId === 'gift', [pageId]);

    const {
        email, emailError, changeEmail, submitEmail,
    } = useEmail();


    useEffect(() => {
        const options = { preferredProvider: paymentWidgetType };
        paymentService.configure(options);
    }, [paymentWidgetType]);

    const basketDishes = getDishesFromBasketSections(basket.sections);
    const basketDishesIds = basketDishes.map((dish) => dish.id);

    const [navigateFlag, setNavigateFlag] = useState(false);
    const [isInvoiceCreated, setIsInvoiceCreated] = useState(false);

    const [isFoodCardFormShown, setIsFoodCardFormShown] = useState(false);

    useEffect(() => {
        if (state.address.value && state.phone.value && state.flat && !navigateFlag) {
            analyticService.push({
                eventName: 'Track_Navigate',
                step: 4,
                sections: basket.sections,
                totals: basket.totals,
                typeOfSet: basket.typeOfSet,
            });
            setNavigateFlag(true);
        }
    }, [state.address.value, state.phone.value, state.flat, navigateFlag, basket.sections, basket.totals, basket.typeOfSet]);

    const handleSelectDate = (nextDate) => {
        checkoutDeliveryDateVar(nextDate);
        selectDateAction({
            state,
            dispatch,
            client,
            options: {
                nextDate,
                ignorePeriodForDeliveryDates,
                period,
            },
            context: {
                updateShipping,
                refetchBasket,
            },
        });
    };

    const handleSelectTimeInterval = (nextInterval) => {
        selectTimeIntervalAction({
            state,
            dispatch,
            client,
            options: { interval: nextInterval },
        });
    };

    /* TODO: унифицировать обработку */
    const handleChangePhone = (e) => {
        const { target: { value } } = e;
        const phone = normalizePhone(value);

        if (phone) {
            sessionStorage.setItem('phone', phone);
        }

        if (parentRef.current) {
            parentRef.current.scrollTo(0, 0);
        }

        changePhoneAction({
            state,
            dispatch,
            client,
            options: { phone },
        });
    };

    const handleChangePromocode = (e) => {
        changePromocodeAction({
            dispatch,
            options: { promocode: e.target.value },
        });
    };
    const handleSubmitPromocode = async (e) => {
        e.preventDefault();

        changeLoadingPromocodeAction({
            dispatch,
            options: { isLoading: true },
        });

        console.log(state.promocode.value, 'executed in checkout handleSubmitPromocode');
        await updateBilling({
            phone: state.phone.value,
            promocode: state.promocode.value,
        });

        changeLoadingPromocodeAction({
            dispatch,
            options: { isLoading: false },
        });

        const [basketResp] = await refetchBasket();

        const {
            data: { cart: { discount_conditions: dc } },
        } = basketResp;

        analyticService.push({
            eventName: 'Check_Send_Promo',
            sourceName: pageId,
            isPromoCodeExists: dc.isPromoCodeExists,
            promocodeStatus: dc.promocode_status,
            isPromocodeCombineWithDiscount: dc.isPromocodeCombineWithDiscount,
            isSubscriptionActivated: dc.isSubscriptionActivated,
            promo: dc.promocode,
        });
    };

    const handleChangeApartment = (e) => {
        const { target: { value } } = e;

        const regex = /([^\d\wa-я])+/gi; // рег для поиска всех НЕ чисел и букв
        const processedValue = value.replace(regex, '');

        changeFlatAction({
            state,
            dispatch,
            client,
            options: {
                flat: processedValue,
            },
            context: {
                updateShipping,
            },
        });
    };
    const handleChangeEntrance = (e) => {
        const { target: { value } } = e;

        const regex = /([^\d\wa-я])+/gi; // рег для поиска всех НЕ чисел и букв
        const processedValue = value.replace(regex, '');

        changeEntranceAction({
            state,
            dispatch,
            client,
            options: {
                entrance: processedValue,
            },
            context: {
                updateShipping,
            },
        });
    };

    const handleBlurPhone = async () => {
        if (state.phone.validation.hasError) return;


        await updateBilling({ phone: state.phone.value });
        await refetchBasket();

        onPhoneEntered(state.phone.value);
    };

    const handleChangeAddress = () => {
        changeAddressAction({
            dispatch,
        });
    };

    const handleSelectAddress = async (address, flat, entrance, options = { refetchBasket: true }) => {
        selectAddressAction({
            state,
            dispatch,
            client,
            options: {
                address,
                entrance,
                flat,
                period,
                trialScenario,
                ignorePeriodForDeliveryDates,
                basketDishesIds,
                isNYPage: pageId === 'blyuda-dlya-novogodnego-stola',
            },
            context: {
                updateShipping,
                refetchBasket: options.refetchBasket && refetchBasket,
                onLocationChange,
            },
        });
    };

    const handleClickPay = useCallback(async (event, paymentMethodArg) => {
        sessionStorage.removeItem('addPaperRecipe');
        if (state.phone.validation.hasError) {
            changePhoneAction({
                state,
                dispatch,
                client,
                options: { isErroVisible: true },
            });
            return;
        }

        const pm = paymentMethodArg || paymentMethod;

        await updateBilling({
            phone: state.phone.value,
            paymentMethod: pm,
        });
        await refetchBasket();

        analyticService.push({ eventName: 'Payment_Button_Click' });

        try {
            const invoice = await createInvoice({
                state,
                dispatch,
                client,
                options: {
                    period,
                    basketDishesIds,
                    paymentMethod: pm,
                    printed_recipes,
                },
            });

            const {
                price,
                uid: invoiceId,
                cp_account_id: accountId,
                isNewbie,
                label,
            } = invoice;

            const userType = isNewbie ? 'new' : 'old';

            const dishesData = getDishesFromBasketSections(basket.sections);

            const userData = userQuery.data?.user || null;

            const trackData = {
                invoiceData: {
                    uid: invoiceId,
                    price,
                    shipping: basket.totals.delivery_price,
                    coupon: basket.promocode,
                    userData,
                    userType,
                    trialScenario,
                    phone: state.phone.value,
                },
                typeOfSet: basket.typeOfSet,
                dishes: dishesData,
                external_subscription_id: label,
                user: userData?.id,
            };

            const fakeEmail = (() => {
                if (!userData) {
                    const phoneVal = getDigitsFromPhone(state.phone.value);
                    return `${phoneVal}@unknown.email`;
                }
                return userData.email || `${userData.phone}@unknown.email`;
            })();

            const flocktoryTrackData = {
                user: {
                    name: userQuery.user ? userQuery.user.name : null,
                    email: fakeEmail,
                },
                order: {
                    id: invoiceId,
                    price,
                    items: dishesData.map((d) => {
                        const {
                            id, title, caption, itemPrice, previewImage, portion,
                        } = d;
                        return {
                            id,
                            title: `${title} ${caption}`,
                            price: itemPrice,
                            image: previewImage,
                            count: portion,
                        };
                    }),
                },
                spot: window.location.pathname,
            };

            analyticService
                .push({ eventName: 'PaymentButtonClickHandled', label })
                .push({ eventName: 'setUserId', userId: accountId })
                // .push({ eventName: 'Invoice_Created', invoiceId })
                .push({ eventName: 'Track_Checkout_User_TYP', userType })
                .push({ eventName: 'Checkout_Sent', ...trackData })
                .push({ eventName: 'Checkout_First_Payment', userType })
                .push({ eventName: 'CityAds_Purchase', ...trackData });

            onInvoiceCreated(true);

            const handlePaymentSuccess = () => {
                setIsInvoiceCreated(true);
                isAsNewbieAuthedVar(false);
                analyticService
                    .push({ eventName: 'Flocktory_Postcheckout', eventParams: flocktoryTrackData })
                    .push({ eventName: 'Track_Purchase', ...trackData });
            };

            if (['cash'].includes(pm)) {
                handlePaymentSuccess();
                setIsFoodCardFormShown(false);
            } else {
                paymentService.showWidget({
                    paymentMethod: 'online',
                    invoiceId,
                    price,
                    accountId,
                    needСreateSubscription: false,
                    onSuccess: handlePaymentSuccess,
                    onFail: (error) => {
                        Raven.captureException(error);
                    },
                });
            }
        } catch (e) {
            console.log(e);
            Raven.captureMessage('createInvoice landing error', {
                extra: { error: e },
            });
            analyticService.push({ eventName: 'Payment_Button_Click_Error' });
        }
    }, [
        state, dispatch,
        trialScenario, period, printed_recipes, pageId, paymentMethod,
        client,
        userQuery.data.user, userQuery.user,
        basketDishesIds, basket.sections, basket.totals.delivery_price, basket.promocode, basket.typeOfSet,
        updateBilling, refetchBasket, changePhoneAction, createInvoice, onInvoiceCreated,
    ]);

    // TODO: это должен быть экшен, но в другой раз
    const initializeStateByUser = async () => {
        const { user } = userQuery.data;

        const getPhone = () => sessionStorage.getItem('phone');

        const phone = getPhone();

        onPhoneInitialized(phone);

        changePhoneAction({
            state,
            dispatch,
            client,
            options: { phone },
        });

        initializeDatesAction({
            state,
            dispatch,
            client,
            nextBeltwayHit: 'inside_mkad',
            options: {
                period,
                trialScenario,
                ignorePeriodForDeliveryDates,
                basketDishesIds,
                isNYPage: pageId === 'blyuda-dlya-novogodnego-stola',
                locationKey,
            },
            isFilterForNYDateNeeded,
        });

        if (!user) return;

        await updateBilling({ phone: state.phone.value });
        await refetchBasket();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!period) return;
        initializeStateByUser();
    }, [
        /*
            Здесь специально перечислены не все зависимости, чтобы хук вызывался только при изменении блюд в корзине
        */
        JSON.stringify(basketDishesIds),
        trialScenario,
        ignorePeriodForDeliveryDates,
        period,
        locationKey,
    ]);
    /* eslint-enable react-hooks/exhaustive-deps */

    useEffect(() => {
        updatePageHeaderPrice(pageId, basket.totals.total_common_price);
    }, [basket.totals.total_common_price, pageId]);

    // TEMPORARY HIDDEN THIS
    // useEffect(
    //     () => {
    //         if (!state.deliveryIntervals.length && !state?.intervalsLoading) {
    //             setNextAvailablePeriod();
    //         }
    //     },
    //     [state.deliveryIntervals.length, state?.intervalsLoading],
    // );

    const showPhoneError = state.phone.validation.hasError && state.phone.isErroVisible;

    const price = basket.totals.total_common_price;
    const priceText = formatPrice(price);

    const enbaledDates = state.deliveryDates.filter((d) => d.enabled);
    const hasEnabledDates = enbaledDates.length > 0;

    const isPaymentButtonDisabledByEmail = useMemo(
        () => (isEmailRequired ? Boolean(emailError) : false),
        [isEmailRequired, emailError],
    );

    const isPaymentButtonDisabled = (
        !hasEnabledDates
        || state.address.validation.hasError
        || price === 0
        || !state.date
        || isPaymentButtonDisabledByEmail
        || hasReadyToEatContradiction
    );

    const inputsContainerClasses = classNames({
        'checkout__inputs-container': true,
        'is-disabled': state.invoice.loading || state.invoice.createdInvoice,
    });

    const { hidePromocodeRow } = config;

    const isPromocodeShown = !hidePromocodeRow && !basket?.totals?.cashback_amount && !basket?.totals?.referral_amount;

    return (
        <div styleName="checkout-container">
            <div styleName={inputsContainerClasses}>
                <div styleName="checkout__input-wrap">
                    <TelephoneInput
                        value={state.phone.value}
                        onChange={handleChangePhone}
                        onBlur={handleBlurPhone}
                        hasError={showPhoneError}
                        errorText={state.phone.validation.errorText}
                        autoFocus={false}
                    />
                </div>

                {isPromocodeShown && (
                    <CheckoutPromocode
                        promocodeValue={state.promocode.value}
                        isPromocodeLoading={state.promocode.isLoading}
                        handleChangePromocode={handleChangePromocode}
                        handleSubmitPromocode={handleSubmitPromocode}
                    />
                )}
                <CheckoutEmail
                    emailValue={email}
                    hasEmailError={Boolean(emailError)}
                    emailErrorText={emailError}
                    handleChangeEmail={(e) => { changeEmail(e.target.value); }}
                    handleBlurEmail={() => { submitEmail(); }}
                />
                <Mobile isWideMobile>
                    <div styleName="checkout__input-wrap">
                        <AddressSelect
                            prepareAddressSuggestions={prepareAddressSuggestions}
                            subdivision={subdivision}
                            address={state.address}
                            onChange={handleChangeAddress}
                            onSelect={handleSelectAddress}
                            placeholder={l.streetInputTitle}
                        />
                    </div>
                    <div styleName="checkout__inputs-row">
                        <div styleName="checkout__input-wrap">
                            <ApartmentInput
                                applyNYStyles
                                value={state.flat}
                                onChange={handleChangeApartment}
                                placeholder={l.flat}
                            />
                        </div>
                        <div styleName="checkout__input-wrap">
                            <EntranceInput
                                value={state.entrance}
                                onChange={handleChangeEntrance}
                                placeholder={l.entrance}
                            />
                        </div>
                    </div>
                </Mobile>

                <Desktop isWideMobile>
                    <div styleName="checkout__inputs-row">
                        <div styleName="checkout__input-wrap checkout__input-wrap--large">
                            <AddressSelect
                                prepareAddressSuggestions={prepareAddressSuggestions}
                                subdivision={subdivision}
                                address={state.address}
                                onChange={handleChangeAddress}
                                onSelect={handleSelectAddress}
                                placeholder={l.streetInputTitle}
                            />
                        </div>

                        <div styleName="checkout__input-wrap checkout__input-wrap--small">
                            <ApartmentInput
                                applyNYStyles
                                value={state.flat}
                                onChange={handleChangeApartment}
                                placeholder={l.flat}
                            />
                        </div>
                    </div>
                </Desktop>

                <div styleName="checkout__input-wrap">
                    {state.deliveryIntervals.length ? (
                        <DeliveryDateTime
                            {...state}
                            onSelectDate={handleSelectDate}
                            onSelectTimeInterval={handleSelectTimeInterval}
                        />
                    ) : null}
                </div>
            </div>

            <div styleName="checkout__input-wrap">
                <div styleName="checkout__button-wrap">
                    {isInvoiceCreated ? (
                        <UIButton
                            sizeType="large"
                            applyNYStyles
                            loading={state.invoice.loading}
                            onClick={() => {
                                window.location = '/profile/';
                            }}
                            aria-label="Перейти в Личный кабинет"
                        >
                            <span className="title">Перейти в Личный кабинет</span>
                        </UIButton>
                    ) : (
                        <UIButton
                            sizeType="large"
                            applyNYStyles
                            loading={state.invoice.loading}
                            onClick={handleClickPay}
                            aria-label="Оформить заказ"
                            disabled={isPaymentButtonDisabled}
                        >
                            <span className="title">
                                {checkoutButtonTextType === 'order' ? l.order : l.pay}
                            </span>
                            <span className="price">
                                <span className="num">{priceText}</span>
                                <span className="rub">₽</span>
                            </span>
                        </UIButton>
                    )}
                </div>
            </div>

            {/* {isInvoiceCreated && (
                <div styleName="checkout__input-wrap">
                    <ThanksPartner />
                </div>
            )} */}
            {/* FRNT-3237 Deprecated */}
            {/* {isFoodcardEnabled && !isInvoiceCreated && (
                <div styleName="checkout__input-wrap">
                    <div styleName="checkout__button-wrap">
                        <UIButton
                            sizeType="large"
                            applyNYStyles
                            onClick={() => {
                                updateBilling({
                                    paymentMethod: 'foodcard',
                                });
                                setIsFoodCardFormShown(true);
                            }}
                            aria-label="Оплатить через FoodCard"
                            disabled={isPaymentButtonDisabled}
                        >
                            <span className="title">
                                Оплата FoodCard
                            </span>
                            <span className="price">
                                <span className="num">{priceText}</span>
                                <span className="rub">₽</span>
                            </span>
                        </UIButton>
                    </div>

                    <FoodcardContainer
                        isFoodCardFormShown={isFoodCardFormShown}
                        cart={basket}
                        selectedPeriod={period}
                        createInvoice={() => {
                            handleClickPay(null, 'foodcard');
                        }}
                    />
                </div>
            )} */}
        </div>
    );
}

// export default Checkout;

const PromoCheckoutDataAccessContainer = (props) => {
    const apolloClient = useApolloClient();
    const filtersContext = useContext(FiltersContext);
    const { period } = filtersContext;

    const config = useContext(ConfigContext);
    const { locationKey } = useContext(LocationContext);
    const {
        basket,
        trialScenario,
    } = useContext(BasketContext);
    const basketDishes = getDishesFromBasketSections(basket.sections);
    const basketDishesIds = basketDishes.map((dish) => dish.id);

    const [updateShipping] = useMutation(SHIPPING);

    const { ignorePeriodForDeliveryDates } = config;

    const {
        selectAddressAction,
        changeFlatAction,
        changeEntranceAction,
    } = createActions({
        config,
        filtersContext,
    });

    const {
        updateBilling,
    } = useContext(BillingContext);

    const [state, dispatch] = useReducer(checkoutReducer, createInitialCheckoutState({ config, period }));

    useQuery(PROMOCODE_FROM_CART_QUERY, {
        variables: { period },
        onCompleted({ cart }) {
            if (cart) {
                const promocodeFromUrl = queryString.parse(window.location.search).promo_code;
                if (config.pageId === 'gift') {
                    updateBilling({
                        promocode: '',
                    });
                    return;
                }

                const isExceptionForPromoPage = config.pageId === 'blyuda-dlya-novogodnego-stola' || config.pageId === 'mealprep';

                if (isExceptionForPromoPage) {
                    updateBilling({
                        promocode: promocodeFromUrl || '',
                    });
                    return;
                }

                const { discount_conditions: { promocode } } = cart;
                // const promocodeFromBasketToSet = localStorage.getItem('promocodeFromBasketToSet');
                const currentPromocode = promocode;
                dispatch({
                    type: 'CHANGE_PROMOCODE',
                    payload: { promocode: currentPromocode },
                });
                updateBilling({
                    promocode: currentPromocode,
                });
            }
        },

        constext: {
            step: 'lp:init:checkout',
        },
    });

    useQuery(INITIAL_ADDRESS_INPUT_VALUE, {
        onCompleted: async ({ deliveryAddress }) => {
            const { address } = deliveryAddress;

            await selectAddressAction({
                state,
                dispatch,
                client: apolloClient,
                options: {
                    address: {
                        value: address.addressValue,
                        data: {
                            house: address.house,
                        },
                    },
                    entrance: address.entrance,
                    flat: address.flat,
                    period,
                    trialScenario,
                    ignorePeriodForDeliveryDates,
                    basketDishesIds,
                    isNYPage: config.pageId === 'blyuda-dlya-novogodnego-stola',
                },
                context: {
                    updateShipping,
                    onLocationChange: props?.onLocationChange,
                },
            });

            if (address?.flat) {
                await changeFlatAction({
                    state,
                    dispatch,
                    client: apolloClient,
                    options: { period, flat: address.flat },
                });
            }


            if (address?.entrance) {
                await changeEntranceAction({
                    state,
                    dispatch,
                    client: apolloClient,
                    options: { period, entrance: address.entrance || '' },
                });
            }
        },

        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'standby',
        constext: {
            step: 'lp:init:checkout',
        },
    });


    return (<Checkout {...props} state={state} dispatch={dispatch} />);
};


Checkout.propTypes = {
    parentRef: PropTypes.shape({
        current: PropTypes.shape({}),
    }),
    onInvoiceCreated: PropTypes.func.isRequired,
    onPhoneEntered: PropTypes.func.isRequired,
    onPhoneInitialized: PropTypes.func.isRequired,
    hasReadyToEatContradiction: PropTypes.bool.isRequired,
    onLocationChange: PropTypes.func.isRequired,
};

export default React.memo(PromoCheckoutDataAccessContainer);
